import { useEffect, useState, type FormEvent } from "react";
import { Copy, Download, Loader2 } from "lucide-react";
import { ApiError, type TotpEnrollResponse } from "@/lib/api";
import { Button, FormField, Input } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";

type Step = "loading" | "qr" | "codes" | "error";

function extractSecret(otpauthUri: string): string | null {
  try {
    const url = new URL(otpauthUri);
    return url.searchParams.get("secret");
  } catch {
    return null;
  }
}

export interface TotpEnrollmentFlowProps {
  onEnroll: () => Promise<TotpEnrollResponse>;
  onConfirm: (enrollmentId: string, code: string) => Promise<string[]>;
  /** Called once the operator has acknowledged storing their recovery codes. */
  onComplete: (recoveryCodes: string[]) => void | Promise<void>;
}

export function TotpEnrollmentFlow({ onEnroll, onConfirm, onComplete }: TotpEnrollmentFlowProps) {
  const [step, setStep] = useState<Step>("loading");
  const [enrollment, setEnrollment] = useState<TotpEnrollResponse | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    setStep("loading");
    onEnroll()
      .then((result) => {
        if (cancelled) return;
        setEnrollment(result);
        setStep("qr");
      })
      .catch(() => {
        if (cancelled) return;
        setStep("error");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleConfirm(event: FormEvent) {
    event.preventDefault();
    if (!enrollment) return;
    setError(null);
    setSubmitting(true);
    try {
      const codes = await onConfirm(enrollment.enrollmentId, code.trim());
      setRecoveryCodes(codes);
      setStep("codes");
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError("Demasiados intentos. Espera unos minutos e intentalo de nuevo.");
      } else {
        setError("El codigo no es valido. Comprueba la hora de tu dispositivo e intentalo de nuevo.");
      }
      setCode("");
    } finally {
      setSubmitting(false);
    }
  }

  function handleCopyCodes() {
    void navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast({ title: "Codigos copiados", variant: "success" });
  }

  function handleDownloadCodes() {
    const blob = new Blob([recoveryCodes.join("\n") + "\n"], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "shalom-app-control-codigos-recuperacion.txt";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  if (step === "loading") {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Generando clave de autenticacion...
      </div>
    );
  }

  if (step === "error") {
    return (
      <p role="alert" className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
        No se pudo iniciar la configuracion del autenticador. Vuelve a intentarlo.
      </p>
    );
  }

  if (step === "codes") {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-warning-200 bg-warning-50 px-3 py-2.5 text-sm text-warning-900">
          Guarda estos codigos ahora. Cada uno se puede usar una sola vez y{" "}
          <strong>no volveran a mostrarse</strong>.
        </div>
        <ul className="grid grid-cols-2 gap-2 rounded-lg border border-ink/10 bg-surface-subtle p-3 font-mono text-sm">
          {recoveryCodes.map((rc) => (
            <li key={rc} className="rounded bg-white px-2 py-1 text-center">
              {rc}
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleCopyCodes} icon={<Copy className="h-4 w-4" aria-hidden="true" />}>
            Copiar
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleDownloadCodes} icon={<Download className="h-4 w-4" aria-hidden="true" />}>
            Descargar
          </Button>
        </div>
        <label className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-ink/30 text-brand-700 focus-visible:ring-2 focus-visible:ring-brand-600/40"
          />
          He guardado estos codigos de recuperacion en un lugar seguro.
        </label>
        <Button
          type="button"
          className="w-full"
          disabled={!acknowledged}
          onClick={() => onComplete(recoveryCodes)}
        >
          Continuar
        </Button>
      </div>
    );
  }

  const secret = enrollment ? extractSecret(enrollment.otpauthUri) : null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-muted">
        Escanea este codigo QR con Google Authenticator, Microsoft Authenticator, Authy o una
        app equivalente.
      </p>
      {enrollment && (
        <div className="flex justify-center">
          <img
            src={enrollment.qrImage}
            alt="Codigo QR para configurar la autenticacion en dos pasos"
            className="h-44 w-44 rounded-lg border border-ink/10 bg-white p-2"
          />
        </div>
      )}
      {secret && (
        <div className="rounded-lg bg-surface-subtle p-3 text-center">
          <p className="text-xs text-ink-faint">O introduce esta clave manualmente</p>
          <p className="mt-1 break-all font-mono text-sm font-medium text-ink">{secret}</p>
        </div>
      )}

      <form onSubmit={handleConfirm} className="space-y-3">
        <FormField label="Codigo de 6 digitos" required hint="Introduce el codigo que muestra tu aplicacion para confirmar la configuracion.">
          {(field) => (
            <Input
              {...field}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="one-time-code"
              required
              // eslint-disable-next-line jsx-a11y/no-autofocus -- sole field on a dedicated code-entry step
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={submitting}
              className="text-center text-lg tracking-[0.3em]"
            />
          )}
        </FormField>
        {error && (
          <p role="alert" className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" loading={submitting}>
          Confirmar y activar
        </Button>
      </form>
    </div>
  );
}
